import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import {
  CreateSkuRequest,
  ApproveNewCostRequest,
  CancelApprovelCostRequest,
} from "../../pages/CreateSku/type";

type RequestType = {
  isLoading: boolean;
  data: CreateSkuRequest | null;
  error: string | null;
  isLoadingDetails: boolean;
  dataDetails: CreateSkuRequest | null;
  errorDetails: string | null;
  isEdit: boolean;
};

interface RequestDetailsFile {
  Action: string;
  SkuId: string;
  ArtifactId: string;
}

const initialState: RequestType = {
  isLoading: false,
  data: null,
  error: "",
  isLoadingDetails: false,
  dataDetails: null,
  errorDetails: "",
  isEdit: false,
};

const isGatewayInvalidInputError = (error: any) =>
  error?.status === 403 ||
  error?.response?.status === 403 ||
  error?.request?.status === 403 ||
  error?.code === "ERR_NETWORK" ||
  error?.message === "Network Error";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSkuDetails = createAsyncThunk<any, any>(
  "CTDBCreateSkuSlice/getSkuDetails",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", {
        SkuId: requestDetails.SkuId,
        ProductId: requestDetails.ProductId,
        Action: "retrieve",
      });
      const successStatusCodes = [200, 201, 202];
      if (
        !successStatusCodes.includes(response.status) ||
        response.data.Status !== "success"
      ) {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to create SKU.",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product sku list",
      });
    }
  },
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createSku = createAsyncThunk<any, CreateSkuRequest>(
  "CTDBCreateSkuSlice/createSku",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", {
        SkuId: requestDetails.SkuId,
        ProductId: requestDetails.ProductId,
        ProductSkuNum: requestDetails.ProductSkuNum,
        Name: requestDetails.Name,
        Description: requestDetails.Description,
        CostMacroCategoryCd: requestDetails.CostMacroCategoryCd,
        CostMetricCd: requestDetails.CostMetricCd,
        CostMetricCost: requestDetails.CostMetricCost,
        CurrencyCd: requestDetails.CurrencyCd,
        CostIntervalCd: requestDetails.CostIntervalCd,
        CostIntervalMin: requestDetails.CostIntervalMin,
        CostIntervalMax: requestDetails.CostIntervalMax,
        IsAutoProvisioned: requestDetails.IsAutoProvisioned,
        IsReturnable: requestDetails.IsReturnable,
        Indexation: requestDetails.Indexation,
        StatusCd: requestDetails.StatusCd,
        SkuRelation: requestDetails.SkuRelation,
        BillingTypeCd: requestDetails.BillingTypeCd,
        CostMetricNSR: requestDetails.CostMetricNSR,
        GlobalRiskMgr: requestDetails.GlobalRiskMgr || {},
        LocalRiskMgr: requestDetails.LocalRiskMgr || {},
        ProductApprover: requestDetails.ProductApprover || {},
        DeploymentApprover: requestDetails.DeploymentApprover || {},
        Action: requestDetails.isEdit ? "edit" : "create",
        ConsumptionMgr: requestDetails.ConsumptionMgr || [],
      });
      const successStatusCodes = [200, 201, 202];
      if (
        !successStatusCodes.includes(response.status) ||
        response.data.Status !== "success"
      ) {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: "Unexpected error occurred while creating SKU",
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Unexpected error occurred while creating SKU.",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (isGatewayInvalidInputError(error)) {
        return thunkApi.rejectWithValue({
          message:
            "Invalid input detected. Please review the input text and try again!",
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Unexpected error occurred while creating SKU",
        });
      }
    }
  },
);

export const approveNewCost = createAsyncThunk<any, ApproveNewCostRequest>(
  "CTDBCreateSkuSlice/approveNewCost",
  async (payload, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/sku", payload);
      const successStatusCodes = [200, 201, 202];
      if (
        !successStatusCodes.includes(response.status) ||
        response.data.Status !== "success"
      ) {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: "Unexpected error occurred while approving new cost.",
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Unexpected error occurred while approving new cost.",
          });
        }
      }
      return response.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue({
        message:
          error?.message ||
          "Unexpected error occurred while approving new cost.",
      });
    }
  },
);

export const cancelApprovelCost = createAsyncThunk<
  any,
  CancelApprovelCostRequest
>("CTDBCreateSkuSlice/cancelapprovelCost", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("/db/product/sku", payload);
    const successStatusCodes = [200, 201, 202];
    if (
      !successStatusCodes.includes(response.status) ||
      response.data.Status !== "success"
    ) {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: "Unexpected error occurred while approving new cost.",
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Unexpected error occurred while approving new cost.",
        });
      }
    }
    return response.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      message:
        error?.message || "Unexpected error occurred while approving new cost.",
    });
  }
});

export const fetchSKUArtifactFileData = createAsyncThunk<any, RequestDetailsFile>(
  "CTDBCreateSkuSlice/fetchSKUArtifactFileData",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post(
        "/db/product/sku",
        requestDetails,
      );

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get SKU artifact file data",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get SKU artifact file data",
      });
    }
  },
);

const CTDBCreateSkuSlice = createSlice({
  name: "CTDBCreateSkuSlice",
  initialState,
  reducers: {
    resetCreateSkuState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSku.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        createSku.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        createSku.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        },
      )
      .addCase(getSkuDetails.pending, (state) => {
        state.isLoadingDetails = true;
        state.errorDetails = null;
        state.dataDetails = null;
      })
      .addCase(
        getSkuDetails.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoadingDetails = false;
            state.dataDetails = action.payload.Data;
            state.dataDetails = null;
          } else if (action.payload.Status == "error") {
            state.isLoadingDetails = false;
            state.dataDetails = null;
            state.dataDetails = action.payload.message;
          }
        },
      )
      .addCase(
        getSkuDetails.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoadingDetails = false;
          state.dataDetails = null;
          state.errorDetails = action.payload.message;
        },
      );
  },
});

export const { resetCreateSkuState } = CTDBCreateSkuSlice.actions;

export default CTDBCreateSkuSlice.reducer;
